import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSafeInterval } from '../hooks';
import './DatabaseRefresh.css';

/**
 * @file DatabaseRefresh.js
 * WHAT: Data collection job management UI for triggering, monitoring, and troubleshooting database refresh operations across all 5 supported technologies.
 * WHY: DAMA must periodically pull user access data from source databases into its central store so that attestation campaigns have current data; this component lets admins define, execute, and monitor those collection jobs.
 */

/**
 * WHAT: Renders a tabbed interface for managing refresh job definitions, viewing execution history with step-by-step progress, and inspecting detailed execution logs.
 * WHY: Database refresh jobs are the data pipeline backbone of DAMA -- without reliable data collection, attestation campaigns would operate on stale user access information, creating compliance risk.
 */
const DatabaseRefresh = () => {
  const { user, hasPermission, canManageRefreshJobs, getAccessibleTechnologies } = useAuth();

  // Technologies this caller can manage refresh jobs for. Dev admins see all;
  // tech DBAs see just their own. Used to filter both the job-definitions list
  // and the executions list so a DBA only sees their technology's activity.
  // Memoized as a Set of uppercased names (database_type values use upper-case,
  // e.g. 'ORACLE', while the AuthContext tech list uses lowercase).
  const accessibleDbTypes = useMemo(() => {
    if (hasPermission('is_dev_admin')) return null; // null == no filter
    const techs = getAccessibleTechnologies().filter(t => canManageRefreshJobs(t));
    return new Set(techs.map(t => t.toUpperCase()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  const restrictByTech = (rows) => {
    if (!accessibleDbTypes) return rows;
    return (rows || []).filter(r => accessibleDbTypes.has((r.database_type || '').toUpperCase()));
  };
  // When the user has access to exactly one technology, push the filter to the
  // server so we don't over-fetch; otherwise fetch all and filter client-side.
  const singleTechFilter = () =>
    accessibleDbTypes && accessibleDbTypes.size === 1
      ? { database_type: [...accessibleDbTypes][0] }
      : {};

  // State Management
  const [activeTab, setActiveTab] = useState('definitions');
  const [jobDefinitions, setJobDefinitions] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [executionLogs, setExecutionLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Configuration State
  const [databaseTypes, setDatabaseTypes] = useState([]);
  const [complianceFrameworks, setComplianceFrameworks] = useState([]);

  // Modal position state for dragging
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Default the technology selector to the caller's own technology when they
  // have exactly one (DBA personas). Dev admins keep the historical ORACLE default.
  const defaultDbType = accessibleDbTypes && accessibleDbTypes.size === 1
    ? [...accessibleDbTypes][0]
    : 'ORACLE';

  // Quick Refresh Form State
  const [quickRefreshForm, setQuickRefreshForm] = useState({
    database_type: defaultDbType,
    compliance_framework: 'CMMC',
    database_names_filter: [],
    triggered_by: ''
  });
  const [availableDatabases, setAvailableDatabases] = useState([]);
  const [showQuickRefreshModal, setShowQuickRefreshModal] = useState(false);

  // Job Definition Modal State
  const [showJobModal, setShowJobModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [jobForm, setJobForm] = useState({
    job_name: '',
    database_type: defaultDbType,
    compliance_framework: 'CMMC',
    schedule_type: 'DAILY',
    schedule_time: '02:00',
    is_enabled: true,
    description: ''
  });

  // Load data on component mount
  useEffect(() => {
    loadDatabaseTypes();
    loadComplianceFrameworks();
    loadJobDefinitions();
    loadRecentExecutions();
  }, []);

  // Set up auto-refresh for active executions using safe interval hook
  useSafeInterval(() => {
    if (activeTab === 'executions' || activeTab === 'active') {
      loadRecentExecutions();
    }
    if (selectedExecution) {
      loadExecutionDetails(selectedExecution);
    }
  }, 5000); // Refresh every 5 seconds

  // Fetch database types
  const loadDatabaseTypes = async () => {
    try {
      const response = await api.config.getDatabaseTypes();
      if (response.data.success) {
        setDatabaseTypes(response.data.database_types);
      }
    } catch (err) {
      console.error('Failed to load database types:', err);
    }
  };

  // Fetch compliance frameworks
  const loadComplianceFrameworks = async () => {
    try {
      const response = await api.config.getComplianceFrameworks();
      if (response.data.success) {
        setComplianceFrameworks(response.data.frameworks);
      }
    } catch (err) {
      console.error('Failed to load compliance frameworks:', err);
    }
  };

  // Fetch job definitions, scoped to the technologies the user can manage.
  const loadJobDefinitions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.databaseRefresh.v2.listDefinitions({
        is_enabled: true,
        ...singleTechFilter()
      });
      if (response.data.success) {
        const visible = restrictByTech(response.data.definitions);
        const sortedDefinitions = [...visible].sort((a, b) =>
          a.job_name.localeCompare(b.job_name)
        );
        setJobDefinitions(sortedDefinitions);
      }
    } catch (err) {
      setError(`Failed to load job definitions: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch recent executions, scoped to the technologies the user can manage.
  const loadRecentExecutions = async (filters = {}) => {
    try {
      const response = await api.databaseRefresh.v2.listExecutions({
        limit: 50,
        ...singleTechFilter(),
        ...filters  // caller-supplied filters win (e.g. the 'RUNNING' button)
      });
      if (response.data.success) {
        setExecutions(restrictByTech(response.data.executions));
      }
    } catch (err) {
      console.error('Failed to load executions:', err);
    }
  };

  // Fetch execution details including steps
  const loadExecutionDetails = async (executionId) => {
    try {
      const response = await api.databaseRefresh.v2.getExecutionDetails(executionId);
      if (response.data.success) {
        setExecutionDetails(response.data.execution);
      }
    } catch (err) {
      console.error('Failed to load execution details:', err);
    }
  };

  // Fetch execution logs
  const loadExecutionLogs = async (executionId, filters = {}) => {
    try {
      const response = await api.databaseRefresh.v2.getExecutionLogs(executionId, {
        limit: 100,
        ...filters
      });
      if (response.data.success) {
        // Sort logs in descending order (most recent first)
        const sortedLogs = [...response.data.logs].sort((a, b) =>
          new Date(b.log_timestamp) - new Date(a.log_timestamp)
        );
        setExecutionLogs(sortedLogs);
      }
    } catch (err) {
      console.error('Failed to load execution logs:', err);
    }
  };

  // Format triggered_by value as "Employee_id - Full Name"
  const getFormattedTriggeredBy = () => {
    if (user) {
      const employeeId = user.employee_id || user.username || 'N/A';
      const fullName = user.full_name || user.username || 'Unknown';
      return `${employeeId} - ${fullName}`;
    }
    return 'admin';
  };

  // Open Quick Refresh modal
  const openQuickRefreshModal = async (definition) => {
    setQuickRefreshForm({
      database_type: definition.database_type,
      compliance_framework: definition.compliance_framework,
      database_names_filter: [],
      triggered_by: getFormattedTriggeredBy()
    });

    // Fetch available databases for this job
    try {
      const response = await api.databaseRefresh.v2.getDatabasesForJob(
        definition.database_type,
        definition.compliance_framework
      );
      if (response.data.success) {
        setAvailableDatabases(response.data.databases);
      }
    } catch (err) {
      console.error('Failed to load databases:', err);
    }

    setShowQuickRefreshModal(true);
  };

  // Create Quick Refresh execution
  const handleQuickRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        database_type: quickRefreshForm.database_type,
        compliance_framework: quickRefreshForm.compliance_framework,
        triggered_by: quickRefreshForm.triggered_by
      };

      // Add database filter if any selected
      if (quickRefreshForm.database_names_filter.length > 0) {
        payload.database_names_filter = quickRefreshForm.database_names_filter;
      }

      const response = await api.databaseRefresh.v2.createQuickRefresh(payload);

      if (response.data.success) {
        alert(`Quick Refresh started!\nExecution ID: ${response.data.execution_id}`);
        setShowQuickRefreshModal(false);
        setActiveTab('executions');
        loadRecentExecutions();
      }
    } catch (err) {
      setError(`Failed to start Quick Refresh: ${err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Toggle database selection for filtering
  const toggleDatabaseSelection = (dbName) => {
    setQuickRefreshForm(prev => {
      const filter = prev.database_names_filter || [];
      if (filter.includes(dbName)) {
        return { ...prev, database_names_filter: filter.filter(d => d !== dbName) };
      } else {
        return { ...prev, database_names_filter: [...filter, dbName] };
      }
    });
  };

  // View execution details
  const viewExecutionDetails = (execution) => {
    setSelectedExecution(execution.execution_id);
    loadExecutionDetails(execution.execution_id);
    loadExecutionLogs(execution.execution_id);
  };

  // Format duration
  const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined) return 'N/A';
    if (seconds === 0) return '< 1s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  // Display employee ID or fallback to email prefix
  const displayEmployeeId = (exec) => {
    if (exec.employee_id) return exec.employee_id;
    if (exec.triggered_by) return exec.triggered_by.split('@')[0];
    return 'N/A';
  };

  // Format datetime with timezone
  const formatDateTimeWithZone = (datetime) => {
    if (!datetime) return 'N/A';
    return new Date(datetime).toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Get status badge color
  const getStatusBadge = (status) => {
    const colors = {
      'PENDING': 'bg-secondary',
      'RUNNING': 'bg-primary',
      'COMPLETED': 'bg-success',
      'FAILED': 'bg-danger',
      'CANCELLED': 'bg-warning',
      'SUCCESS': 'bg-success'
    };
    return colors[status] || 'bg-secondary';
  };

  // Get available compliance frameworks for a database type
  const getAvailableComplianceFrameworks = (databaseType) => {
    // Get all active framework labels from the fetched data
    const allFrameworks = complianceFrameworks
      .filter(fw => fw.is_active)
      .map(fw => fw.label);

    // Filter out frameworks that already have jobs for this database type
    const existingFrameworks = jobDefinitions
      .filter(job => job.database_type === databaseType)
      .map(job => job.compliance_framework);

    return allFrameworks.filter(fw => !existingFrameworks.includes(fw));
  };

  // Auto-generate job name from database type and compliance framework
  const generateJobName = (databaseType, complianceFramework) => {
    return `${databaseType} - ${complianceFramework}`;
  };

  // Job Definition Management Functions
  const openCreateJobModal = () => {
    setIsEditMode(false);

    // Get the first active database type the caller can manage. Dev admins
    // see everything; tech DBAs start with their own technology.
    const activeDbTypes = databaseTypes
      .filter(type => type.is_active)
      .filter(type => !accessibleDbTypes || accessibleDbTypes.has(String(type.label).toUpperCase()));
    const initialDbType = activeDbTypes.length > 0 ? activeDbTypes[0].label : defaultDbType;

    const availableFrameworks = getAvailableComplianceFrameworks(initialDbType);
    const initialFramework = availableFrameworks.length > 0 ? availableFrameworks[0] : 'CMMC';

    setJobForm({
      job_name: generateJobName(initialDbType, initialFramework),
      database_type: initialDbType,
      compliance_framework: initialFramework,
      schedule_type: 'DAILY',
      schedule_time: '02:00',
      is_enabled: true,
      description: ''
    });
    setShowJobModal(true);
  };

  const openEditJobModal = (job) => {
    setIsEditMode(true);
    // Strip seconds from schedule_time for display in time input (HH:MM:SS -> HH:MM)
    const displayTime = job.schedule_time && job.schedule_time.split(':').length === 3
      ? job.schedule_time.substring(0, 5)
      : job.schedule_time;

    setJobForm({
      job_name: job.job_name,
      database_type: job.database_type,
      compliance_framework: job.compliance_framework,
      schedule_type: job.schedule_type,
      schedule_time: displayTime,
      is_enabled: job.is_enabled,
      description: job.description || ''
    });
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    setLoading(true);
    setError(null);
    try {
      // Format schedule_time to include seconds (HH:MM:SS)
      const formattedJobData = {
        ...jobForm,
        schedule_time: jobForm.schedule_time.includes(':') && jobForm.schedule_time.split(':').length === 2
          ? `${jobForm.schedule_time}:00`
          : jobForm.schedule_time
      };

      if (isEditMode) {
        // Update existing job
        await api.databaseRefresh.v2.updateDefinition(
          jobForm.database_type,
          jobForm.compliance_framework,
          formattedJobData
        );
        alert(`Job "${jobForm.job_name}" updated successfully!`);
      } else {
        // Create new job
        await api.databaseRefresh.v2.createDefinition(formattedJobData);
        alert(`Job "${jobForm.job_name}" created successfully!`);
      }
      setShowJobModal(false);
      loadJobDefinitions();
    } catch (err) {
      setError(`Failed to ${isEditMode ? 'update' : 'create'} job: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteJob = async (job) => {
    if (!window.confirm(`Are you sure you want to delete the job "${job.job_name}"?\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await api.databaseRefresh.v2.deleteDefinition(
        job.database_type,
        job.compliance_framework
      );
      alert(`Job "${job.job_name}" deleted successfully!`);
      loadJobDefinitions();
    } catch (err) {
      setError(`Failed to delete job: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCleanupFailedExecutions = async () => {
    if (!window.confirm('Are you sure you want to delete all failed executions?\n\nThis action cannot be undone.')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await api.databaseRefresh.v2.cleanupFailedExecutions();
      if (response.data.success) {
        const count = response.data.deleted_count || 0;
        const stepsDeleted = response.data.steps_deleted || 0;
        const logsDeleted = response.data.logs_deleted || 0;

        alert(`Successfully deleted ${count} failed execution(s)\n\nDetails:\n- Executions: ${count}\n- Steps: ${stepsDeleted}\n- Logs: ${logsDeleted}`);
        loadRecentExecutions();
      }
    } catch (err) {
      setError(`Failed to cleanup failed executions: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Render Job Definitions Tab
  const renderDefinitionsTab = () => (
    <div className="p-4">
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="fas fa-calendar-alt me-2"></i>
            Scheduled Job Definitions
          </h5>
          <div className="d-flex gap-2">
            <button
              onClick={openCreateJobModal}
              className="btn btn-light btn-sm"
            >
              <i className="fas fa-plus me-1"></i> New Job
            </button>
            <button
              onClick={loadJobDefinitions}
              className="btn btn-outline-light btn-sm"
            >
              <i className="fas fa-sync-alt me-1"></i> Refresh
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">Loading...</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-hover">
            <thead className="table-dark">
              <tr>
                <th>Job Name</th>
                <th>Database Type</th>
                <th>Compliance</th>
                <th>Schedule</th>
                <th>Next Run</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobDefinitions.map((job, idx) => (
                <tr key={idx}>
                  <td className="fw-medium">{job.job_name}</td>
                  <td>{job.database_type}</td>
                  <td>{job.compliance_framework}</td>
                  <td>
                    {job.schedule_type} @ {job.schedule_time}
                  </td>
                  <td>
                    {job.next_run_time ? new Date(job.next_run_time).toLocaleString() : 'N/A'}
                  </td>
                  <td>
                    <span className={`badge ${job.is_enabled ? 'bg-success' : 'bg-secondary'}`}>
                      {job.is_enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        onClick={() => openQuickRefreshModal(job)}
                        className="btn btn-primary btn-sm"
                        title="Run a quick refresh for this job"
                      >
                        Quick Refresh
                      </button>
                      <button
                        onClick={() => openEditJobModal(job)}
                        className="btn btn-warning btn-sm"
                        title="Edit job definition"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteJob(job)}
                        className="btn btn-danger btn-sm"
                        title="Delete job definition"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {jobDefinitions.length === 0 && (
            <div className="text-center py-5 text-muted">
              No job definitions found
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render Executions Tab
  const renderExecutionsTab = () => (
    <div className="p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h5 mb-0">Recent Executions</h2>
        <div className="d-flex gap-2">
          <button
            onClick={() => loadRecentExecutions({ execution_status: 'RUNNING' })}
            className="btn btn-primary"
          >
            Show Running
          </button>
          <button
            onClick={() => loadRecentExecutions()}
            className="btn btn-secondary"
          >
            Show All
          </button>
          <button
            onClick={handleCleanupFailedExecutions}
            className="btn btn-danger"
            title="Delete all failed executions and their associated data"
          >
            Clean Up Failed
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead className="table-dark">
            <tr>
              <th>Execution ID</th>
              <th>Job Name</th>
              <th>Type</th>
              <th>Status</th>
              <th>Triggered By</th>
              <th>Started</th>
              <th style={{ width: '80px' }}>Duration</th>
              <th style={{ width: '200px' }}>Results</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {executions.map((exec) => (
              <tr key={exec.execution_id}>
                <td className="font-monospace small">
                  {exec.execution_id.substring(0, 8)}...
                </td>
                <td className="fw-medium">{exec.job_name}</td>
                <td>{exec.execution_type}</td>
                <td>
                  <span className={`badge ${getStatusBadge(exec.execution_status)}`}>
                    {exec.execution_status}
                  </span>
                </td>
                <td style={{ maxWidth: '150px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                  {displayEmployeeId(exec)}
                </td>
                <td>
                  {formatDateTimeWithZone(exec.started_at)}
                </td>
                <td>{formatDuration(exec.duration_seconds)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div className="small">
                    <div>✅ {exec.databases_succeeded} / ❌ {exec.databases_failed}</div>
                    <div className="text-muted">Users: {exec.total_users_refreshed}</div>
                  </div>
                </td>
                <td>
                  <button
                    onClick={() => viewExecutionDetails(exec)}
                    className="btn btn-success btn-sm"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {executions.length === 0 && (
          <div className="text-center py-5 text-muted">
            No executions found
          </div>
        )}
      </div>
    </div>
  );

  // Modal drag handlers
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('modal-header') || e.target.closest('.modal-header')) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - modalPosition.x,
        y: e.clientY - modalPosition.y
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setModalPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Reset modal position when closing
  const closeExecutionDetailsModal = () => {
    setSelectedExecution(null);
    setExecutionDetails(null);
    setModalPosition({ x: 0, y: 0 });
  };

  // Render Execution Details Modal
  const renderExecutionDetailsModal = () => {
    if (!executionDetails) return null;

    return ReactDOM.createPortal(
      <div
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={closeExecutionDetailsModal}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div
          style={{
            margin: 0,
            maxWidth: '95vw',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '2px solid #333',
            transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.1s ease-out'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              cursor: 'move',
              userSelect: 'none',
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
              padding: '20px',
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
            onMouseDown={handleMouseDown}
          >
            <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Execution Details</h5>
            <button
              onClick={closeExecutionDetailsModal}
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0', lineHeight: '1' }}
              aria-label="Close"
            >×</button>
          </div>
          <div style={{ padding: '20px', flex: '1 1 auto', overflowY: 'auto' }}>

            {/* Execution Summary */}
            <div className="bg-light p-4 rounded mb-4">
              <div className="row g-4">
                <div className="col-12 col-sm-6 col-lg-3">
                  <div className="small text-muted">Execution ID</div>
                  <div className="font-monospace small">{executionDetails.execution_id}</div>
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <div className="small text-muted">Job Name</div>
                  <div className="fw-medium">{executionDetails.job_name}</div>
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <div className="small text-muted">Status</div>
                  <span className={`badge ${getStatusBadge(executionDetails.execution_status)}`}>
                    {executionDetails.execution_status}
                  </span>
                </div>
                <div className="col-12 col-sm-6 col-lg-3">
                  <div className="small text-muted">Duration</div>
                  <div>{formatDuration(executionDetails.duration_seconds)}</div>
                </div>
              </div>

              <div className="row g-4 mt-3">
                <div className="col-6 col-lg-3">
                  <div className="small text-muted">Total Databases</div>
                  <div className="h4 fw-bold">{executionDetails.total_databases || 0}</div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="small text-muted">Succeeded</div>
                  <div className="h4 fw-bold text-success">{executionDetails.databases_succeeded || 0}</div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="small text-muted">Failed</div>
                  <div className="h4 fw-bold text-danger">{executionDetails.databases_failed || 0}</div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="small text-muted">Users Refreshed</div>
                  <div className="h4 fw-bold text-primary">{executionDetails.total_users_refreshed || 0}</div>
                </div>
              </div>

              {executionDetails.error_message && (
                <div className="alert alert-danger mt-4">
                  <div className="fw-bold">Error</div>
                  <div>{executionDetails.error_message}</div>
                </div>
              )}
            </div>

            {/* Execution Steps */}
            <div className="mb-4">
              <h3 className="h6 fw-bold mb-3">Execution Steps</h3>
              <div className="d-flex flex-column gap-2">
                {executionDetails.steps && executionDetails.steps.map((step) => (
                  <div key={step.step_order} className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3">
                        <div className="fw-bold text-dark">#{step.step_order}</div>
                        <div className="fw-medium">{step.step_name}</div>
                        <span className={`badge ${getStatusBadge(step.step_status)}`}>
                          {step.step_status}
                        </span>
                      </div>
                      <div className="small text-muted">
                        {formatDuration(step.duration_seconds)}
                      </div>
                    </div>

                    {step.database_name && (
                      <div className="mt-2 small">
                        <span className="text-muted">Database:</span> {step.database_name}
                      </div>
                    )}

                    {step.records_processed > 0 && (
                      <div className="mt-2 small">
                        <span className="text-muted">Records Processed:</span> {step.records_processed}
                        {step.records_succeeded > 0 && (
                          <span className="ms-3 text-success">✓ {step.records_succeeded} succeeded</span>
                        )}
                        {step.records_failed > 0 && (
                          <span className="ms-3 text-danger">✗ {step.records_failed} failed</span>
                        )}
                      </div>
                    )}

                    {step.error_message && (
                      <div className="mt-2 alert alert-danger py-2 small mb-0">
                        {step.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Execution Logs */}
            <div>
              <h3 className="h6 fw-bold mb-3">Execution Logs</h3>
              <div className="bg-dark text-success p-4 rounded font-monospace small overflow-auto" style={{ maxHeight: '24rem' }}>
                {executionLogs.length > 0 ? (
                  executionLogs.map((log, idx) => (
                    <div key={log.log_id || idx} className="mb-2">
                      <div>
                        <span className="text-secondary">{new Date(log.log_timestamp).toLocaleTimeString()}</span>
                        {' '}
                        <span className={`fw-bold ${
                          log.log_level === 'ERROR' ? 'text-danger' :
                          log.log_level === 'WARNING' ? 'text-warning' :
                          log.log_level === 'INFO' ? 'text-info' :
                          'text-muted'
                        }`}>[{log.log_level || 'INFO'}]</span>
                        {' '}
                        {log.step_order && <span style={{ color: '#9b59b6' }}>[Step {log.step_order}]</span>}
                        {' '}
                        {log.log_category && <span className="text-info">[{log.log_category}]</span>}
                        {' '}
                        <span className="text-success">{log.message || log.log_message || 'No message'}</span>
                      </div>
                      {log.details && typeof log.details === 'object' && Object.keys(log.details).length > 0 && (
                        <div className="ms-4 mt-1 text-muted small">
                          <pre className="text-wrap mb-0">{JSON.stringify(log.details, null, 2)}</pre>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-muted py-4">No logs available</div>
                )}
              </div>
            </div>
            </div>

            {/* Modal Footer with Restart Button */}
            <div style={{ borderTop: '1px solid #dee2e6', padding: '16px 20px', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {executionDetails.execution_status === 'FAILED' && executionDetails.can_restart && (
                <button
                  onClick={async () => {
                    if (window.confirm('Are you sure you want to restart this execution from the failed step?')) {
                      try {
                        const response = await api.databaseRefresh.v2.restartExecution(executionDetails.execution_id);
                        if (response.data.success) {
                          alert(`Execution restarted! New Execution ID: ${response.data.new_execution_id.substring(0, 8)}...`);
                          closeExecutionDetailsModal();
                          loadRecentExecutions();
                        }
                      } catch (error) {
                        alert('Failed to restart execution: ' + (error.response?.data?.error || error.message));
                      }
                    }
                  }}
                  style={{ padding: '8px 16px', backgroundColor: '#ffc107', color: '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <span>↻</span> Restart from Failed Step
                </button>
              )}
              <button
                onClick={closeExecutionDetailsModal}
                style={{ padding: '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginLeft: 'auto' }}
              >
                Close
              </button>
            </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Quick Refresh Modal
  const renderQuickRefreshModal = () => {
    if (!showQuickRefreshModal) return null;

    return ReactDOM.createPortal(
      <div
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={() => setShowQuickRefreshModal(false)}
      >
        <div
          style={{
            margin: 0,
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '2px solid #333'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ borderBottom: '1px solid #dee2e6', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>Quick Refresh</h5>
            <button
              type="button"
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0', lineHeight: '1' }}
              onClick={() => setShowQuickRefreshModal(false)}
              aria-label="Close"
            >×</button>
          </div>

          <div style={{ padding: '20px', overflowY: 'auto', flex: '1 1 auto' }}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Job</label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {quickRefreshForm.database_type} - {quickRefreshForm.compliance_framework}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select Databases (Optional)</label>
              <div style={{ fontSize: '0.875rem', color: '#6c757d', marginBottom: '0.5rem' }}>
                Leave empty to refresh all compliant databases
              </div>
              <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', padding: '0.75rem', maxHeight: '12rem', overflowY: 'auto' }}>
                {availableDatabases.map((db) => (
                  <div key={db.database_name} style={{ marginBottom: '0.5rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={quickRefreshForm.database_names_filter.includes(db.database_name)}
                        onChange={() => toggleDatabaseSelection(db.database_name)}
                        style={{ marginRight: '0.5rem' }}
                      />
                      {db.database_name} ({db.database_type})
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Triggered By</label>
              <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                {quickRefreshForm.triggered_by}
              </div>
            </div>

            {error && (
              <div style={{ padding: '0.75rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginBottom: '1rem' }}>
                {error}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #dee2e6', padding: '16px 20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowQuickRefreshModal(false)}
              style={{ padding: '8px 16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleQuickRefresh}
              disabled={loading}
              style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}
            >
              {loading ? 'Starting...' : 'Start Refresh'}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  // Render Job Definition Modal
  const renderJobModal = () => {
    if (!showJobModal) return null;

    // Get available compliance frameworks for current database type (create mode only)
    const availableFrameworks = !isEditMode
      ? getAvailableComplianceFrameworks(jobForm.database_type)
      : [];

    // Handle database type change
    const handleDatabaseTypeChange = (newDbType) => {
      const frameworks = getAvailableComplianceFrameworks(newDbType);
      const newFramework = frameworks.length > 0 ? frameworks[0] : jobForm.compliance_framework;

      setJobForm({
        ...jobForm,
        database_type: newDbType,
        compliance_framework: newFramework,
        job_name: generateJobName(newDbType, newFramework)
      });
    };

    // Handle compliance framework change
    const handleComplianceFrameworkChange = (newFramework) => {
      setJobForm({
        ...jobForm,
        compliance_framework: newFramework,
        job_name: generateJobName(jobForm.database_type, newFramework)
      });
    };

    return ReactDOM.createPortal(
      <div
        tabIndex="-1"
        style={{
          backgroundColor: 'rgba(0,0,0,0.7)',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
        onClick={() => setShowJobModal(false)}
      >
        <div
          style={{
            margin: 0,
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            border: '2px solid #333'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ borderBottom: '1px solid #dee2e6', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>{isEditMode ? 'Edit Job Schedule' : 'Create New Job Definition'}</h5>
            <button
              type="button"
              style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', padding: '0', lineHeight: '1' }}
              onClick={() => setShowJobModal(false)}
              aria-label="Close"
            >×</button>
          </div>

          <div style={{ padding: '20px', overflowY: 'auto', flex: '1 1 auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Database Type *</label>
                <select
                  value={jobForm.database_type}
                  onChange={(e) => handleDatabaseTypeChange(e.target.value)}
                  disabled={isEditMode}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                >
                  {databaseTypes
                    .filter(type => type.is_active)
                    .filter(type => !accessibleDbTypes || accessibleDbTypes.has(String(type.label).toUpperCase()))
                    .map(type => (
                      <option key={type.value} value={type.label}>
                        {type.label}
                      </option>
                    ))
                  }
                </select>
                {isEditMode && <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>Database type cannot be changed after creation</small>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Compliance Framework *</label>
                {isEditMode ? (
                  <>
                    <select
                      value={jobForm.compliance_framework}
                      disabled={true}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                    >
                      <option value={jobForm.compliance_framework}>{jobForm.compliance_framework}</option>
                    </select>
                    <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>Compliance framework cannot be changed after creation</small>
                  </>
                ) : (
                  <>
                    <select
                      value={jobForm.compliance_framework}
                      onChange={(e) => handleComplianceFrameworkChange(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                    >
                      {availableFrameworks.length > 0 ? (
                        availableFrameworks.map(fw => (
                          <option key={fw} value={fw}>{fw}</option>
                        ))
                      ) : (
                        <option value="">No available frameworks</option>
                      )}
                    </select>
                    {availableFrameworks.length === 0 && (
                      <small style={{ color: '#dc3545', fontSize: '0.875rem' }}>All compliance frameworks already have jobs for this database type</small>
                    )}
                  </>
                )}
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Job Name</label>
                <div style={{ padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                  {jobForm.job_name}
                </div>
                {!isEditMode && <small style={{ color: '#6c757d', fontSize: '0.875rem' }}>Auto-generated from database type and compliance framework</small>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Schedule Type *</label>
                <select
                  value={jobForm.schedule_type}
                  onChange={(e) => setJobForm({ ...jobForm, schedule_type: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                >
                  <option value="DAILY">Daily</option>
                  <option value="WEEKLY">Weekly</option>
                  <option value="MONTHLY">Monthly</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Schedule Time *</label>
                <input
                  type="time"
                  value={jobForm.schedule_time}
                  onChange={(e) => setJobForm({ ...jobForm, schedule_time: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da' }}
                />
              </div>

              {!isEditMode && (
                <>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Description</label>
                    <textarea
                      value={jobForm.description}
                      onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                      rows="3"
                      placeholder="Optional description of this job"
                      style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ced4da', resize: 'vertical' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={jobForm.is_enabled}
                        onChange={(e) => setJobForm({ ...jobForm, is_enabled: e.target.checked })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Enable this job (will run automatically on schedule)
                    </label>
                  </div>
                </>
              )}
            </div>

            {error && (
              <div style={{ padding: '0.75rem', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px', marginTop: '1rem' }}>
                {error}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #dee2e6', padding: '16px 20px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowJobModal(false)}
              disabled={loading}
              style={{ padding: '8px 16px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveJob}
              disabled={loading || (!isEditMode && (!jobForm.job_name || availableFrameworks.length === 0))}
              title={!isEditMode && availableFrameworks.length === 0 ? 'No available compliance frameworks for this database type' : ''}
              style={{ padding: '8px 16px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: (loading || (!isEditMode && (!jobForm.job_name || availableFrameworks.length === 0))) ? 'not-allowed' : 'pointer', opacity: (loading || (!isEditMode && (!jobForm.job_name || availableFrameworks.length === 0))) ? 0.6 : 1 }}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Schedule' : 'Create Job')}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="database-refresh container-fluid py-4 px-4">
      <h1 className="h3 mb-4">Database Refresh Management</h1>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            onClick={() => setActiveTab('definitions')}
            className={`nav-link ${activeTab === 'definitions' ? 'active' : ''}`}
            style={{
              fontWeight: activeTab === 'definitions' ? 'bold' : 'normal',
              backgroundColor: activeTab === 'definitions' ? '#4e73df' : 'transparent',
              color: activeTab === 'definitions' ? '#ffffff' : '#4e73df',
              border: activeTab === 'definitions' ? '2px solid #4e73df' : '2px solid #e3e6f0',
              borderBottom: activeTab === 'definitions' ? 'none' : '2px solid #e3e6f0',
              transition: 'all 0.3s ease',
              fontSize: '1.05rem',
              padding: '0.75rem 1.5rem'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'definitions') {
                e.target.style.backgroundColor = '#e8edf7';
                e.target.style.color = '#2e59d9';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'definitions') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#4e73df';
              }
            }}
          >
            <span style={{ marginRight: '8px' }}>📋</span>
            Job Definitions
          </button>
        </li>
        <li className="nav-item">
          <button
            onClick={() => setActiveTab('executions')}
            className={`nav-link ${activeTab === 'executions' ? 'active' : ''}`}
            style={{
              fontWeight: activeTab === 'executions' ? 'bold' : 'normal',
              backgroundColor: activeTab === 'executions' ? '#1cc88a' : 'transparent',
              color: activeTab === 'executions' ? '#ffffff' : '#1cc88a',
              border: activeTab === 'executions' ? '2px solid #1cc88a' : '2px solid #e3e6f0',
              borderBottom: activeTab === 'executions' ? 'none' : '2px solid #e3e6f0',
              transition: 'all 0.3s ease',
              fontSize: '1.05rem',
              padding: '0.75rem 1.5rem'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'executions') {
                e.target.style.backgroundColor = '#d4f4e7';
                e.target.style.color = '#17a673';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'executions') {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#1cc88a';
              }
            }}
          >
            <span style={{ marginRight: '8px' }}>▶️</span>
            Executions
          </button>
        </li>
      </ul>

      {/* Tab Content */}
      {activeTab === 'definitions' && renderDefinitionsTab()}
      {activeTab === 'executions' && renderExecutionsTab()}

      {/* Modals */}
      {renderJobModal()}
      {renderQuickRefreshModal()}
      {renderExecutionDetailsModal()}
    </div>
  );
};

export default DatabaseRefresh;
